import { z } from 'zod';
import { ReportReasonSchema, ReportTargetSchema, REPORT_REASONS } from '../reports';
import { AdminPageQuerySchema } from './page';

/* Reports queue */
export const ReportStatusSchema = z.enum(['open', 'actioned', 'dismissed']);
export type ReportStatus = z.infer<typeof ReportStatusSchema>;

export const ReportSeveritySchema = z.enum(['low', 'medium', 'high']);
export type ReportSeverity = z.infer<typeof ReportSeveritySchema>;

export const AdminReportSchema = z.object({
  id: z.string(),
  target: ReportTargetSchema,
  targetId: z.string(),
  reason: ReportReasonSchema,
  detail: z.string().nullable(),
  /** The reporter-supplied content snapshot — the only message text admins see. */
  snapshot: z.string().nullable(),
  /** Set only when the reporter attached a Whisper image — the only image an
   * admin can ever fetch, via `/api/admin/reports/:id/image`. */
  hasSnapshotImage: z.boolean(),
  status: ReportStatusSchema,
  severity: ReportSeveritySchema,
  reportedUserId: z.string().nullable(),
  reportedUserHandle: z.string().nullable(),
  reportedUserPriorReports: z.number().int().nonnegative(),
  claimedBy: z.string().nullable(),
  claimedByHandle: z.string().nullable(),
  escalated: z.boolean(),
  createdAt: z.iso.datetime(),
});
export type AdminReport = z.infer<typeof AdminReportSchema>;

/** One reported user with their open-report count — the `?groupBy=user` shape. */
export const AdminReportGroupSchema = z.object({
  reportedUserId: z.string(),
  reportedUserHandle: z.string().nullable(),
  openReports: z.number().int().nonnegative(),
  priorSanctions: z.number().int().nonnegative(),
  highestSeverity: ReportSeveritySchema,
  latestAt: z.iso.datetime(),
});
export type AdminReportGroup = z.infer<typeof AdminReportGroupSchema>;

export const AdminReportsResponseSchema = z.object({
  items: z.array(AdminReportSchema),
  /** Populated only for `?groupBy=user`; `null` otherwise. */
  groups: z.array(AdminReportGroupSchema).nullable(),
  nextCursor: z.string().nullable(),
  total: z.number().int().nonnegative().nullable(),
});
export type AdminReportsResponse = z.infer<typeof AdminReportsResponseSchema>;

/** Filters + paging accepted by GET /api/admin/reports. */
export const AdminReportsQuerySchema = AdminPageQuerySchema.extend({
  status: ReportStatusSchema.default('open'),
  reason: ReportReasonSchema.optional(),
  severity: ReportSeveritySchema.optional(),
  target: ReportTargetSchema.optional(),
  reportedUserId: z.string().optional(),
  claimedBy: z.string().optional(),
  groupBy: z.literal('user').optional(),
});
export type AdminReportsQuery = z.infer<typeof AdminReportsQuerySchema>;

export const MOD_ACTIONS = [
  'dismiss',
  'warn',
  'mute_24h',
  'mute_7d',
  'suspend_30d',
  'ban',
  'remove_message',
  'freeze_room',
  'close_room',
] as const;
export const ModActionSchema = z.enum(MOD_ACTIONS);
export type ModAction = z.infer<typeof ModActionSchema>;

/* POST /api/admin/reports/:id/action */
export const TakeActionSchema = z.object({
  action: ModActionSchema,
  /** Required justification — written to the immutable audit log. */
  reason: z.string().min(3).max(500),
});
export type TakeAction = z.infer<typeof TakeActionSchema>;

/* POST /api/admin/reports/bulk-action */
export const BulkReportActionSchema = z.object({
  ids: z.array(z.string()).min(1).max(50),
  action: ModActionSchema,
  reason: z.string().min(3).max(500),
});
export type BulkReportAction = z.infer<typeof BulkReportActionSchema>;

/** Report reasons re-exported for admin filter UIs. */
export { REPORT_REASONS };
